'use client';
import { apiSdkAtom } from '@/context/atoms/ApiSdk';
import { publicConfigAtom } from '@/context/atoms/publicConfig';
import { MessageEvent, type SocketIOEventsMap, SubscriptionPayload } from '@/context/messaging/socket-io';
import createContextHardened from '@/helper/createContextHardened';
import { useAtom } from 'jotai';
import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { io as ioSocket, type Socket } from 'socket.io-client';
import { authMechanismAtom } from '@/context/atoms/AuthMechanism';
import useStableCallback from '@/helper/useStableCallback';
import { WebEamAuthMechanism } from '@/helper/auth/WebEamAuthMechanism';
import { authenticationAtom } from '@/context/atoms/Authentication';

export interface MqttSubscription<T = unknown> {
  payload: SubscriptionPayload;
  eventHandler: (data: T) => void;
  onAcknowledge?: (subscription: SubscriptionPayload) => void;
}

interface SocketIOContextState {
  isConnected: boolean;
  isConnecting: boolean;
  showIndicator: boolean;
  connect: () => void;
  disconnect: () => void;
  subscribe: (key: string, subscription: MqttSubscription) => void;
  unsubscribe: (key: string) => void;
  subscriptions: Record<string, MqttSubscription>;
}

const [SocketIOContext, useSocketIO] = createContextHardened<SocketIOContextState>('SocketIO');
export { useSocketIO };

export type SocketIOProviderProps = PropsWithChildren;

export function SocketIOProvider({ children }: SocketIOProviderProps): React.JSX.Element {
  const [api] = useAtom(apiSdkAtom);
  const [publicConfig] = useAtom(publicConfigAtom);
  const [authMechanism] = useAtom(authMechanismAtom);
  const [, setAuthentication] = useAtom(authenticationAtom);

  const authToken = useMemo(() => authMechanism.authentication.authToken, [authMechanism.authentication.authToken]);
  useEffect(() => {
    console.log(authToken);
  }, [authToken]);

  const [isConnecting, startConnecting] = useTransition();
  const [isConnected, setConnected] = useState<boolean>(false);
  const [showIndicator, setShowIndicator] = useState<SocketIOContextState['showIndicator']>(false);
  const [subscriptions, setSubscriptions] = useState<Record<string, MqttSubscription>>({});

  const socketRef = useRef<Socket<SocketIOEventsMap>>(null);

  // refactored to a single socket-io instance. as of now, we don't need multiple socket connections
  const connect = useCallback((): void => {
    setShowIndicator(true);

    console.log('Initiating socket.io connection');

    if (isConnecting) {
      return;
    }

    startConnecting(() => {
      if (socketRef.current !== null) {
        socketRef.current.connect();
      }
    });
  }, [isConnecting]);

  const disconnect = useCallback(() => {
    console.log('Called socket.io disconnet');
    setShowIndicator(false);

    if (socketRef.current !== null) {
      socketRef.current.disconnect();
    }
  }, []);

  const handleBrowserEvent = useStableCallback(
    (eventType: 'visibility' | 'online') => {
      if (eventType === 'visibility' && document.visibilityState !== 'visible') {
        return;
      }

      console.log('Browser Event: ' + eventType);
      if (!isConnected) {
        console.log('Reconnecting via provider (handles token refresh)...');
        connect();
      }
    },
    [isConnected, connect],
  );

  const unsubscribe = useCallback((key: string): void => {
    setSubscriptions((prev) => {
      const subscription = prev[key];

      if (subscription !== undefined && socketRef.current !== null) {
        // Emit unsubscribe to server so it stops forwarding MQTT messages
        if (socketRef.current.connected) {
          socketRef.current.emit(MessageEvent.MQTT_UNSUBSCRIBE, subscription.payload);
        }

        socketRef.current.off(subscription.payload.type, subscription.eventHandler);

        if (subscription.onAcknowledge !== undefined) {
          socketRef.current.off(MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED, subscription.onAcknowledge);
        }

        const { [key]: _, ...reduced } = prev;

        console.log('MQTT subscription removed:', key);
        return reduced;
      }

      return prev;
    });
  }, []);

  const subscribe = useCallback(
    (key: string, subscription: MqttSubscription): void => {
      setSubscriptions((prev) => {
        if (socketRef.current === null) {
          return prev;
        }
        // If subscription already exists, unsubscribe first
        if (prev !== undefined && prev[key] !== undefined) {
          unsubscribe(key);
        }

        // Set up event listeners - cast via variable to satisfy Socket.IO's type signature
        const handler: (data: unknown) => void = subscription.eventHandler;

        socketRef.current.on(subscription.payload.type, handler);
        if (subscription.onAcknowledge !== undefined) {
          socketRef.current.on(MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED, subscription.onAcknowledge);
        }

        // Add to registry
        return { ...prev, [key]: subscription };
      });
    },
    [unsubscribe],
  );

  useEffect(() => {
    const hostname = publicConfig.env.SOCKET_IO_HOST ?? window.location.hostname;
    const socketIoPort = publicConfig.env.SOCKET_IO_PORT ?? 3030;
    const socketIoServer = `//${hostname}:${socketIoPort}`;

    socketRef.current = ioSocket(socketIoServer, {
      reconnection: false,
      autoConnect: false,
      auth: {
        authToken,
      },
    });

    socketRef.current.on('connect_error', async (error) => {
      console.log('socket.io connection error');
      console.log('connect_error details:', error);
      setConnected(false);

      // Maybe "error?.message?.includes('(401)') || error?.description === 401" is required to also catch TransportError from engine.io
      const isUnauthorized = error?.message?.includes('(401)');

      if (isUnauthorized) {
        console.log('Authentication expired, refreshing token...');

        if (authMechanism instanceof WebEamAuthMechanism) {
          // WebEamAuth is unable to refresh the access_token at the time being.
          // So we need to trigger a logout of the full worker ui app, here.
          console.log('WebEamAuthMechanism cannot refresh. Logging out...');
          //await authMechanism.invalidateAuthentication();
          setAuthentication({
            isAuthenticated: false,
            authToken: undefined,
            user: undefined,
          });
          return;
        }

        // Call getCurrentUser() to trigger an HTTP request that will:
        // 1. Be caught by the 401 interceptor
        // 2. Automatically refresh the token
        // 3. Update the auth token atom
        await api.getCurrentUser();
        console.log('Token refreshed, retrying connection...');

        connect();
      }
    });

    socketRef.current.on('connect', () => {
      console.log('socket.io connected');
      setConnected(true);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('socket.io disconnected:', reason);
      setConnected(false);

      // Reconnect for any non-intentional disconnect (server disconnect, transport close, ping timeout, etc.)
      // Only skip reconnect if we explicitly called disconnect() ('io client disconnect')
      if (reason !== 'io client disconnect' && socketRef.current !== null) {
        console.log('Attempting to reconnect after disconnect:', reason);
        socketRef.current.connect();
      }
    });

    connect();

    // Event listeners use stableHandleBrowserEvent which always calls the latest callback
    const onVisibilityChange = (): void => handleBrowserEvent('visibility');
    const onOnline = (): void => handleBrowserEvent('online');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      if (socketRef.current !== null) {
        socketRef.current.disconnect();
        socketRef.current.removeAllListeners();
        socketRef.current = null;
      }
    };
    /** This hook has to run exactly once on-mount. Therefore no dependencies! */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (socketRef.current !== null) {
      socketRef.current.auth = {
        authToken,
      };

      console.log('socket.io auth token replaced');
    }
  }, [authToken]);

  // Resubscribe whenever subscriptions change or socket connects
  useEffect(() => {
    if (isConnected && socketRef.current?.connected && Object.keys(subscriptions).length !== 0) {
      console.log('Resubscribing due to subscription or connection change:', subscriptions);

      Object.entries(subscriptions).forEach(([key, subscription]) => {
        if (socketRef.current === null) {
          return;
        }
        console.log('Emitting subscription for:', key);
        socketRef.current.emit(MessageEvent.MQTT_SUBSCRIBE, subscription.payload);
      });
    }
  }, [subscriptions, isConnected]);

  return (
    <SocketIOContext.Provider
      value={{
        isConnected,
        isConnecting,
        showIndicator,
        connect,
        disconnect,
        subscribe,
        unsubscribe,
        subscriptions,
      }}
    >
      {children}
    </SocketIOContext.Provider>
  );
}
